import React, {
  Fragment,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";
import {
  any,
  equals,
  includes,
  lensPath,
  lensProp,
  path,
  remove,
  set,
  view,
} from "ramda";
import { notBlank, notEmpty } from "../../../util/ext";
import { useCategories } from "../../vocabularyServices";
import { MediaContext } from "../../common/media-context";
import { matchSorter } from "match-sorter";
import { FixedSizeList } from "react-window";

import Autocomplete from "@material-ui/lab/Autocomplete";
import Grid from "@material-ui/core/Grid";
import ListItemText from "@material-ui/core/ListItemText";
import Skeleton from "@material-ui/lab/Skeleton";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";

import AudienceSelect from "../../common/AudienceSelect";

const LISTBOX_PADDING = 8; // px

function renderRow(props) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

// Adapter for react-window
const ListboxComponent = React.forwardRef(function ListboxComponent(
  props,
  ref,
) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 80;

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemSize * itemCount;
  };

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <FixedSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={itemSize}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </FixedSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const Categories = props => {
  const context = useContext(MediaContext);
  const categories = useCategories();
  const { className, showAudience, showFourCategories } = props;

  const [required, setRequired] = useState(true);
  useEffect(() => {
    const type = context.media.type;
    const required = includes("categories", context.requiredFields[type]);
    setRequired(required);
  }, [context.requiredFields, context.media.type]);

  const getOptionDisabled = option =>
    any(category => option.id === category.id, context.media.categories);

  const handleCategoryChange = useCallback(
    path => (event, value) => {
      if (notBlank(value)) {
        context.setMedia(prev => set(lensPath(path), value, prev));
      } else {
        const [list, index] = path;
        context.setMedia(prev =>
          set(
            lensProp(list),
            remove(index, 1, view(lensProp(list), prev)),
            prev,
          ),
        );
      }
    },
    [context],
  );

  const handleTextChange = useCallback(
    path => event => {
      const value = event.target.value;
      context.setMedia(prev => set(lensPath(path), value, prev));
    },
    [context],
  );

  const filterOptions = (options, { inputValue }) =>
    matchSorter(options, inputValue, {
      keys: ["headingDescription", "bisacCode"],
    });

  return (
    <div className={className}>
      <Grid item xs={12}>
        <Typography variant="h6">Categories</Typography>
      </Grid>

      {notEmpty(context.media) && (
        <Fragment>
          <Grid item xs={12}>
            {categories.isLoading ? (
              <Skeleton variant="text" />
            ) : (
              <Autocomplete
                disableClearable
                options={categories.data}
                value={path(["categories", 0], context.media) || ""}
                getOptionLabel={cat =>
                  notBlank(cat) ? cat.headingDescription : ""
                }
                getOptionSelected={(opt, val) =>
                  notBlank(val) && equals(opt.id, val.id)
                }
                getOptionDisabled={getOptionDisabled}
                ListboxComponent={ListboxComponent}
                onChange={handleCategoryChange(["categories", 0])}
                filterOptions={filterOptions}
                renderOption={cat => (
                  <Tooltip
                    title={cat.headingDescription}
                    placement="bottom-start"
                  >
                    <ListItemText
                      primaryTypographyProps={{ noWrap: true }}
                      primary={cat.headingDescription}
                      secondary={cat.bisacCode}
                    />
                  </Tooltip>
                )}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = null;
                  return (
                    <TextField
                      {...params}
                      required={required}
                      type="text"
                      onBlur={() =>
                        context.setErrors(prev =>
                          context.validator.validateField(
                            context.media.type,
                            "categories",
                            context.media.categories[0],
                            prev,
                          ),
                        )
                      }
                      error={context.validator.hasError(
                        context.errors,
                        "categories",
                      )}
                      helperText={context.validator.firstError(
                        context.errors,
                        "categories",
                      )}
                      name={"media-category1-" + Math.random()}
                      variant="standard"
                      label="Category 1"
                      fullWidth
                    />
                  );
                }}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            {categories.isLoading ? (
              <Skeleton variant="text" />
            ) : (
              <Autocomplete
                options={categories.data}
                disabled={context.media.categories.length < 1}
                value={path(["categories", 1], context.media) || ""}
                getOptionLabel={cat =>
                  notBlank(cat) ? cat.headingDescription : ""
                }
                getOptionSelected={(opt, val) =>
                  notBlank(val) && equals(opt.id, val.id)
                }
                getOptionDisabled={getOptionDisabled}
                ListboxComponent={ListboxComponent}
                onChange={handleCategoryChange(["categories", 1])}
                filterOptions={filterOptions}
                renderOption={cat => (
                  <Tooltip
                    title={cat.headingDescription}
                    placement="bottom-start"
                  >
                    <ListItemText
                      primaryTypographyProps={{ noWrap: true }}
                      primary={cat.headingDescription}
                      secondary={cat.bisacCode}
                    />
                  </Tooltip>
                )}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = null;
                  return (
                    <TextField
                      {...params}
                      type="text"
                      name={"media-category2-" + Math.random()}
                      variant="standard"
                      label="Category 2"
                      fullWidth
                    />
                  );
                }}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            {categories.isLoading ? (
              <Skeleton variant="text" />
            ) : (
              <Autocomplete
                disabled={context.media.categories.length < 2}
                autoComplete
                options={categories.data}
                value={path(["categories", 2], context.media) || ""}
                getOptionLabel={cat =>
                  notBlank(cat) ? cat.headingDescription : ""
                }
                getOptionSelected={(opt, val) =>
                  notBlank(val) && equals(opt.id, val.id)
                }
                getOptionDisabled={getOptionDisabled}
                ListboxComponent={ListboxComponent}
                onChange={handleCategoryChange(["categories", 2])}
                filterOptions={filterOptions}
                renderOption={cat => (
                  <Tooltip
                    title={cat.headingDescription}
                    placement="bottom-start"
                  >
                    <ListItemText
                      primaryTypographyProps={{ noWrap: true }}
                      primary={cat.headingDescription}
                      secondary={cat.bisacCode}
                    />
                  </Tooltip>
                )}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = null;
                  return (
                    <TextField
                      {...params}
                      type="text"
                      name={"media-category3-" + Math.random()}
                      variant="standard"
                      label="Category 3"
                      fullWidth
                    />
                  );
                }}
              />
            )}
          </Grid>
          {showFourCategories && (
            <Grid item xs={12}>
              {categories.isLoading ? (
                <Skeleton variant="text" />
              ) : (
                <Autocomplete
                  disabled={context.media.categories.length < 3}
                  autoComplete
                  options={categories.data}
                  value={path(["categories", 3], context.media) || ""}
                  getOptionLabel={cat =>
                    notBlank(cat) ? cat.headingDescription : ""
                  }
                  getOptionSelected={(opt, val) =>
                    notBlank(val) && equals(opt.id, val.id)
                  }
                  getOptionDisabled={getOptionDisabled}
                  ListboxComponent={ListboxComponent}
                  onChange={handleCategoryChange(["categories", 3])}
                  filterOptions={filterOptions}
                  renderOption={cat => (
                    <Tooltip
                      title={cat.headingDescription}
                      placement="bottom-start"
                    >
                      <ListItemText
                        primaryTypographyProps={{ noWrap: true }}
                        primary={cat.headingDescription}
                        secondary={cat.bisacCode}
                      />
                    </Tooltip>
                  )}
                  renderInput={params => {
                    params.inputProps["aria-autocomplete"] = "none";
                    params.inputProps["id"] = null;
                    return (
                      <TextField
                        {...params}
                        type="text"
                        name={"media-category4-" + Math.random()}
                        variant="standard"
                        label="Category 4"
                        fullWidth
                      />
                    );
                  }}
                />
              )}
            </Grid>
          )}
          {showAudience && (
            <Grid item xs={12}>
              <AudienceSelect
                value={path(["audienceCode"], context.media)}
                onChange={handleTextChange(["audienceCode"])}
              />
            </Grid>
          )}
        </Fragment>
      )}
    </div>
  );
};

export default Categories;
